import {
  Armchair,
  ArrowRight,
  Building2,
  Car,
  Check,
  Clock3,
  Coffee,
  CircleOff,
  Gem,
  Handshake,
  Star,
  Users,
} from "lucide-react";

type RequirementIcon = typeof Building2;

const REQUIREMENT_ICONS: Array<[RegExp, RequirementIcon]> = [
  [/meeting rooms?/i, Users],
  [/parking/i, Car],
  [/24\s*\/\s*7|access/i, Clock3],
  [/furnished/i, Armchair],
  [/reception|catering|service/i, Coffee],
  [/private/i, Handshake],
  [/short.?term/i, Star],
  [/amenit/i, Gem],
  [/^none$/i, CircleOff],
];

function getRequirementIcon(requirement: string): RequirementIcon {
  return (
    REQUIREMENT_ICONS.find(([pattern]) => pattern.test(requirement))?.[1] ??
    Building2
  );
}

interface MultiSelectRequirementsProps {
  options: string[];
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  onSubmit: () => void;
  disabled?: boolean;
}

export function MultiSelectRequirements({
  options,
  selected,
  onSelectionChange,
  onSubmit,
  disabled = false,
}: MultiSelectRequirementsProps) {
  if (options.length === 0) return null;
  const displayedOptions = [
    ...new Set(options.filter((option) => option.trim().toLowerCase() !== "none")),
    "None",
  ];

  const toggleOption = (option: string) => {
    if (option === "None") {
      onSelectionChange(selected.includes(option) ? [] : ["None"]);
      return;
    }

    onSelectionChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected.filter((item) => item !== "None"), option],
    );
  };

  return (
    <section className='mb-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4'>
      <h3 className='mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800'>
        <Building2
          className='h-5 w-5 shrink-0 text-oh-primary'
          aria-hidden='true'
        />
        <span>
          Select any additional requirements{" "}
          <span className='font-normal'>(multiple options allowed)</span>
        </span>
      </h3>

      <div className='grid gap-1.5 sm:max-w-md'>
        {displayedOptions.map((option) => {
          const isSelected = selected.includes(option);
          const Icon = getRequirementIcon(option);

          return (
            <button
              key={option}
              type='button'
              role='checkbox'
              aria-checked={isSelected}
              onClick={() => toggleOption(option)}
              disabled={disabled}
              className='flex min-h-8 items-center gap-2 rounded-xl border border-gray-200 px-2.5 py-1 text-left text-sm text-slate-800 transition-colors hover:border-oh-primary/50 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border ${
                  isSelected
                    ? "border-oh-primary bg-oh-primary text-white"
                    : "border-gray-300 bg-white"
                }`}
              >
                {isSelected && (
                  <Check className='h-3.5 w-3.5' aria-hidden='true' />
                )}
              </span>
              <Icon
                className='h-4 w-4 shrink-0 text-slate-700'
                aria-hidden='true'
              />
              <span>{option}</span>
            </button>
          );
        })}
      </div>

      <div className='mt-3 flex justify-end'>
        <button
          type='button'
          onClick={onSubmit}
          disabled={disabled || selected.length === 0}
          className='flex h-9 items-center gap-2 rounded-xl bg-oh-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-oh-primary/90 disabled:cursor-not-allowed disabled:opacity-50'
        >
          Next
          <ArrowRight className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
    </section>
  );
}
